import type React from "react";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, type ModalProps, Modal as MuiModal, Slide, Stack, css, styled as muiStyled } from "@mui/material";
import styled from "styled-components";

import { useWindowSize } from "utils/hooks";

interface Props extends Omit<ModalProps, "children" | "title"> {
  children?: React.ReactNode;
  title?: React.ReactNode;
  showCloseButton?: boolean;
  closeIcon?: React.ReactNode;
  width?: string;
  status?: React.ReactNode;
}

export const MUIUnifiedModal: React.FC<Props> = ({
  open,
  onClose,
  children,
  title,
  showCloseButton = true,
  closeIcon,
  width = "70%",
  status,
  ...rest
}) => {
  const { isSmallScreen } = useWindowSize();

  if (isSmallScreen) {
    return (
      <MuiModal
        open={open}
        onClose={onClose}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
          },
        }}
        {...rest}
      >
        <Slide in={open} direction="up" mountOnEnter unmountOnExit timeout={400}>
          <FullScreenBox data-testid="modal">
            {(title || showCloseButton) && (
              <FullScreenHeader direction="row" alignItems="center">
                {showCloseButton && (
                  <CloseButton onClick={() => onClose?.({}, "backdropClick")} data-testid="close-button">
                    {closeIcon ?? <FontAwesomeIcon icon={faXmark} />}
                  </CloseButton>
                )}
                <TitleBox>{title}</TitleBox>
                <StatusBox>{status}</StatusBox>
              </FullScreenHeader>
            )}
            <FullScreenContent>{children}</FullScreenContent>
          </FullScreenBox>
        </Slide>
      </MuiModal>
    );
  }

  return (
    <CenteredModal open={open} onClose={onClose} {...rest}>
      <CenteredBox width={width} onClick={e => e.stopPropagation()}>
        {showCloseButton && (
          <CenteredHeader direction="row" alignItems="center">
            <TitleBox sx={{ textAlign: "center", pl: 2 }}>{title}</TitleBox>
            {showCloseButton && (
              <CloseButton onClick={() => onClose?.({}, "backdropClick")} data-testid="close-button">
                {closeIcon ?? <FontAwesomeIcon icon={faXmark} />}
              </CloseButton>
            )}
          </CenteredHeader>
        )}
        <CenteredContent>{children}</CenteredContent>
      </CenteredBox>
    </CenteredModal>
  );
};

const fadeIn = css`
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translate(-50%, -48%);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%);
    }
  }
`;

const CenteredModal = styled(MuiModal)`
  display: flex;
  align-items: center;
  justify-content: center;

  & .MuiBackdrop-root {
    background-color: rgba(111, 127, 147, 0.7);
  }
`;

const CenteredBox = muiStyled(Box, {
  shouldForwardProp: prop => prop !== "width",
})<{ width?: string }>`
  ${fadeIn}
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${({ width }) => width ?? "70%"};
  min-width: 300px;
  max-width: calc(100% - 32px);
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: fadeIn 0.2s ease-out;
  outline: none;
`;

const CenteredHeader = styled(Stack)`
  height: 46px;
  flex-shrink: 0;
`;

const CenteredContent = styled(Box)`
  overflow: auto;
  flex: 1;
`;

export const FullScreenBox = styled(Box)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-height: 95vh;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  background-color: ${({ theme }) => theme.colors.white};
  overflow: hidden;
  outline: none;
  display: flex;
  flex-direction: column;
  border-radius: 16px 16px 0 0;
`;

const FullScreenHeader = styled(Stack)`
  height: 46px;
  flex-shrink: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.redesign.db30};
`;

const FullScreenContent = styled(Box)`
  flex: 1;
  overflow: auto;
`;

const TitleBox = styled(Box)`
  flex: 1;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusBox = styled(Box)`
  width: 46px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const CloseButton = styled(Box)`
  width: 46px;
  height: 46px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  flex-shrink: 0;
`;
